<script lang="ts" setup>
import { ref, computed } from "vue";
import { useOrgComponents } from "@/queries/components";
import { useComponentLibrary } from "@/composables/useComponentLibrary";
import type { ComponentType, ComponentGroup } from "@/types";

const { components, componentsLoading: loading, componentsError: error, refetchComponents } = useOrgComponents();

const searchQuery = ref("");
const selectedTypes = ref<ComponentType[]>([]);
const selectedCategories = ref<string[]>([]);
const selectedProducts = ref<string[]>([]);

const { componentGroups, componentStats } = useComponentLibrary(components, {
  searchQuery,
  selectedTypes,
  selectedCategories,
  selectedProducts,
});

const selectedGroup = ref<string | null>(null);
const selectedComponent = ref<any>(null);
const showComponentDetails = ref(false);

const availableTypes: { id: ComponentType; label: string; icon: string }[] = [
  { id: "service", label: "Services", icon: "bi-gear" },
  { id: "connection", label: "Connections", icon: "bi-link" },
  { id: "datastore", label: "Data Stores", icon: "bi-database" },
];

const filteredGroups = computed(() => {
  if (!selectedGroup.value) return componentGroups.value;
  return componentGroups.value.filter(
    (group) =>
      group.productId === selectedGroup.value ||
      (selectedGroup.value === "system" && group.source === "system")
  );
});

function selectComponent(component: any) {
  selectedComponent.value = component;
  showComponentDetails.value = true;
}

function handleSearch(event: Event) {
  const target = event.target as HTMLInputElement;
  searchQuery.value = target.value;
}

function toggleTypeFilter(type: ComponentType) {
  const i = selectedTypes.value.indexOf(type);
  if (i >= 0) selectedTypes.value.splice(i, 1);
  else selectedTypes.value.push(type);
}

function clearFilters() {
  searchQuery.value = "";
  selectedTypes.value = [];
  selectedCategories.value = [];
  selectedProducts.value = [];
  selectedGroup.value = null;
}

function getGroupIcon(group: ComponentGroup): string {
  if (group.source === "system") return "bi-cpu";
  return "bi-box-seam";
}

function getComponentIcon(component: any): string {
  const iconMap: { [key: string]: string } = {
    service: "bi-gear",
    connection: "bi-link",
    datastore: "bi-database",
  };
  return component.icon || iconMap[component.type] || "bi-box";
}
</script>

<template>
  <div class="components-library d-flex flex-column">

    <!-- Search and filters -->
    <div class="components-header border-bottom p-3 mx-n3">
      <div class="d-flex gap-2 mb-3">
        <span class="badge bg-primary">{{ componentStats.accessible }} accessible</span>
        <span class="badge bg-primary">{{ componentStats.total }} total</span>
      </div>

      <!-- Search -->
      <div class="row g-3">
        <div class="col-md-6">
          <div class="input-group">
            <span class="input-group-text"><i class="bi bi-search"></i></span>
            <input
              type="text"
              class="form-control"
              placeholder="Search components..."
              :value="searchQuery"
              @input="handleSearch"
            />
          </div>
        </div>

        <!-- Type filters -->
        <div class="col-md-6">
          <div class="d-flex align-items-center gap-3 flex-wrap">
            <span class="text-muted small fw-medium">Filter by type:</span>
            <div class="d-flex gap-2 flex-wrap">
              <div
                v-for="type in availableTypes"
                :key="type.id"
                class="form-check form-check-inline mb-0"
              >
                <input
                  class="form-check-input"
                  type="checkbox"
                  :id="`filter-${type.id}`"
                  :value="type.id"
                  :checked="selectedTypes.includes(type.id)"
                  @change="toggleTypeFilter(type.id)"
                />
                <label class="form-check-label small" :for="`filter-${type.id}`">
                  <i :class="type.icon" class="me-1"></i>
                  {{ type.label }}
                </label>
              </div>
            </div>

            <button
              v-if="searchQuery || selectedTypes.length > 0 || selectedGroup"
              class="bb-btn-secondary-cta btn-sm ms-2"
              @click="clearFilters"
            >
              <i class="bi bi-x-circle me-1"></i> Clear all
            </button>
          </div>
        </div>
      </div>

      <!-- Group selector -->
      <div class="mt-3">
        <div class="row align-items-center">
          <div class="col-auto">
            <label for="groupSelect" class="form-label mb-0 text-muted small fw-medium">Filter by product:</label>
          </div>
          <div class="col-auto">
            <select
              id="groupSelect"
              v-model="selectedGroup"
              class="form-select form-select-sm group-select"
            >
              <option :value="null">All Products</option>
              <option value="system">System Components</option>
              <option
                v-for="group in componentGroups.filter((g) => g.source === 'product')"
                :key="group.productId"
                :value="group.productId"
              >
                {{ group.productName }} ({{ group.components.length }})
              </option>
            </select>
          </div>
        </div>
      </div>

      <!-- Active filters summary -->
      <div
        v-if="searchQuery || selectedTypes.length > 0 || selectedGroup"
        class="mt-3 pt-2 border-top"
      >
        <div class="d-flex align-items-center gap-2 text-muted small">
          <i class="bi bi-funnel"></i>
          <span>Active filters:</span>
          <div class="d-flex flex-wrap gap-1">
            <span v-if="searchQuery" class="badge bg-info">
              <i class="bi bi-search me-1"></i>"{{ searchQuery }}"
            </span>
            <span v-if="selectedTypes.length > 0" class="badge bg-info">
              <i class="bi bi-funnel me-1"></i>{{ selectedTypes.length }} type{{ selectedTypes.length > 1 ? "s" : "" }}
            </span>
            <span v-if="selectedGroup" class="badge bg-info">
              <i class="bi bi-box me-1"></i>{{ selectedGroup === "system" ? "System" : componentGroups.find((g) => g.productId === selectedGroup)?.productName }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="flex-grow-1 d-flex align-items-center justify-content-center">
      <div class="text-center">
        <div class="spinner-border text-primary mb-3" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="text-muted">Loading components...</p>
      </div>
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="flex-grow-1 d-flex align-items-center justify-content-center">
      <div class="text-center">
        <i class="bi bi-exclamation-triangle text-danger fs-1 mb-3"></i>
        <h3 class="text-danger">Failed to load components</h3>
        <p class="text-muted">{{ error }}</p>
        <button class="bb-btn-primary-cta btn-sm" @click="refetchComponents()">
          <i class="bi bi-arrow-clockwise me-1"></i> Retry
        </button>
      </div>
    </div>

    <!-- Components content -->
    <div v-else class="components-content overflow-auto">
      <div class="p-3">
        <!-- Component groups -->
        <div
          v-for="group in filteredGroups"
          :key="group.productId || 'system'"
          class="component-group mb-4"
        >
          <div class="d-flex align-items-center justify-content-between mb-3">
            <div class="d-flex align-items-center gap-2">
              <i :class="getGroupIcon(group)" class="text-primary"></i>
              <h3 class="h4 mb-0">
                {{ group.source === "system" ? "System Components" : group.productName }}
              </h3>
              <span class="text-muted small">({{ group.components.length }} components)</span>
            </div>
          </div>

          <!-- Components grid -->
          <div class="row g-3">
            <div
              v-for="component in group.components"
              :key="component.id"
              class="col-md-6 col-lg-4"
            >
              <div class="component-card card h-100" @click="selectComponent(component)">
                <div class="card-body p-3">
                  <div class="d-flex align-items-start gap-3">
                    <div class="component-icon d-flex align-items-center justify-content-center">
                      <i :class="getComponentIcon(component)" class="text-primary"></i>
                    </div>
                    <div class="flex-grow-1">
                      <div class="mb-1">
                        <h4 class="card-title mb-0">{{ component.name }}</h4>
                      </div>
                      <p class="card-text text-muted small mb-2">{{ component.description }}</p>
                      <div
                        v-if="component.usageMetrics?.monthlyUses && component.usageMetrics.monthlyUses > 0"
                        class="text-muted small"
                      >
                        <i class="bi bi-graph-up me-1"></i>
                        {{ component.usageMetrics.monthlyUses }} uses this month
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Empty state for group -->
          <div v-if="group.components.length === 0" class="text-center py-4">
            <i class="bi bi-inbox text-muted fs-1 mb-3"></i>
            <p class="text-muted">No components found in this group</p>
          </div>
        </div>

        <!-- Empty state for search -->
        <div v-if="filteredGroups.length === 0" class="text-center py-5">
          <i class="bi bi-search text-muted fs-1 mb-3"></i>
          <h3 class="text-muted">No components found</h3>
          <p class="text-muted">Try adjusting your search or filters</p>
          <button class="bb-btn-secondary-cta btn-sm" @click="clearFilters">
            Clear filters
          </button>
        </div>
      </div>
    </div>

    <!-- Component details modal -->
    <div
      v-if="showComponentDetails && selectedComponent"
      class="modal fade show d-block"
      @click.self="showComponentDetails = false"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <div class="d-flex align-items-center gap-3">
              <i :class="getComponentIcon(selectedComponent)" class="text-primary fs-4"></i>
              <div>
                <h2 class="modal-title mb-0">{{ selectedComponent.name }}</h2>
                <small class="text-muted">{{ selectedComponent.type }} • {{ selectedComponent.productName }}</small>
              </div>
            </div>
            <button type="button" class="btn-close" @click="showComponentDetails = false"></button>
          </div>

          <div class="modal-body">
            <div class="row g-4">
              <div class="col-12">
                <h3>Description</h3>
                <p>{{ selectedComponent.description }}</p>
              </div>

              <div class="col-md-6">
                <h3>Details</h3>
                <ul class="list-unstyled">
                  <li><strong>Type:</strong> {{ selectedComponent.type }}</li>
                  <li><strong>Source:</strong> {{ selectedComponent.source }}</li>
                  <li><strong>Product:</strong> {{ selectedComponent.productName }}</li>
                </ul>
              </div>

              <div class="col-md-6" v-if="selectedComponent.usageCount || selectedComponent.lastUsed">
                <h3>Usage Statistics</h3>
                <ul class="list-unstyled">
                  <li v-if="selectedComponent.usageCount">
                    <strong>Monthly uses:</strong> {{ selectedComponent.usageCount }}
                  </li>
                  <li v-if="selectedComponent.lastUsed">
                    <strong>Last used:</strong> {{ new Date(selectedComponent.lastUsed).toLocaleString() }}
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div class="modal-footer">
            <button type="button" class="bb-btn-cancel" @click="showComponentDetails = false">
              Close
            </button>
            <button type="button" class="bb-btn-secondary-cta" @click="showComponentDetails = false">
              <i class="bi bi-book me-1"></i> View Documentation
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showComponentDetails && selectedComponent" class="modal-backdrop fade show" @click="showComponentDetails = false"></div>
  </div>
</template>

<style scoped lang="scss">
.components-header {
  background: var(--bs-secondary-bg);
}

.component-group {
  background: var(--bs-body-bg);
  border-radius: 8px;
}

.component-card {
  transition: all 0.15s ease-in-out;
  border: 1px solid var(--bs-border-color);
  cursor: pointer;
}

.component-card:hover {
  border-color: var(--bs-primary);
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  background-color: var(--bs-tertiary-bg);
}

.component-icon {
  width: 32px;
  height: 32px;
  background: var(--bs-primary-bg-subtle);
  border-radius: 6px;
  font-size: 16px;
  flex-shrink: 0;
}

.cursor-pointer {
  cursor: pointer;
}

.group-select {
  min-width: 200px;
}
</style>
